// miniapp/src/client/lib/paths.ts  (reminders removed)
import { withBase, API_BASE } from "./config";

export type Route =
  | { name: "root" }
  | { name: "doorHome" }
  | { name: "doorScan" }
  | { name: "doorToken"; token: string }
  | { name: "guestHome" }
  | { name: "guestEula" }
  | { name: "superHome" }
  // events
  | { name: "superEventsHome" }
  | { name: "superEventNew" }
  | { name: "superEventEdit"; id: number }
  // staff
  | { name: "superStaffHome" }
  | { name: "superStaffRoles" }
  // messages & promo
  | { name: "superMessages" }
  | { name: "superPromo" }
  | { name: "superSettings" };

export const ROUTE = {
  // base
  root(): Route { return { name: "root" }; },
  door(): Route { return { name: "doorHome" }; },
  scan(): Route { return { name: "doorScan" }; },
  token(t: string): Route { return { name: "doorToken", token: t }; },
  guest(): Route { return { name: "guestHome" }; },
  eula(): Route { return { name: "guestEula" }; },
  super(): Route { return { name: "superHome" }; },

  // events
  events(): Route { return { name: "superEventsHome" }; },
  eventNew(): Route { return { name: "superEventNew" }; },
  eventEdit(id: number): Route { return { name: "superEventEdit", id }; },

  // staff
  staff(): Route { return { name: "superStaffHome" }; },
  staffRoles(): Route { return { name: "superStaffRoles" }; },

  // comms
  messages(): Route { return { name: "superMessages" }; },
  promo(): Route { return { name: "superPromo" }; },
  settings(): Route { return { name: "superSettings" }; },

  // --- predicates (used by UI components) ---
  isDoorHome(r?: Route) { const x = r || ROUTE.parse(); return x.name === "doorHome"; },
  isDoorScan(r?: Route) { const x = r || ROUTE.parse(); return x.name === "doorScan"; },
  isDoorToken(r?: Route) { const x = r || ROUTE.parse(); return x.name === "doorToken"; },
  isGuest(r?: Route) { const x = r || ROUTE.parse(); return x.name === "guestHome" || x.name === "guestEula"; },
  isSuperHome(r?: Route) { const x = r || ROUTE.parse(); return x.name === "superHome"; },
  isSuperMessages(r?: Route) { const x = r || ROUTE.parse(); return x.name === "superMessages"; },
  isSuperPromo(r?: Route) { const x = r || ROUTE.parse(); return x.name === "superPromo"; },
  isSuperSettings(r?: Route) { const x = r || ROUTE.parse(); return x.name === "superSettings"; },
  isSuperEvents(r?: Route) { const x = r || ROUTE.parse(); return x.name === "superEventsHome" || x.name === "superEventNew" || x.name === "superEventEdit"; },

  toPath(r: Route): string {
    switch (r.name) {
      case "root":              return withBase("/");
      case "doorHome":          return withBase("/door");
      case "doorScan":          return withBase("/door/scan");
      case "doorToken":         return withBase(`/door/t/${encodeURIComponent(r.token)}`);

      case "guestHome":         return withBase("/guest");
      case "guestEula":         return withBase("/guest/eula");

      case "superHome":         return withBase("/super");

      case "superEventsHome":   return withBase("/super/events");
      case "superEventNew":     return withBase("/super/events/new");
      case "superEventEdit":    return withBase(`/super/events/${r.id}/edit`);

      case "superStaffHome":    return withBase("/super/staff");
      case "superStaffRoles":   return withBase("/super/staff/roles");

      case "superMessages":     return withBase("/super/messages");
      case "superPromo":        return withBase("/super/promo");
      case "superSettings":     return withBase("/super/settings");
    }
  },

  parse(pathname = window.location.pathname): Route {
    const p = stripBase(pathname);

    // base
    if (p === "/" || p === "") return ROUTE.root();
    if (p === "/door" || p === "/door/") return ROUTE.door();
    if (p === "/door/scan") return ROUTE.scan();
    if (p.startsWith("/door/t/")) {
      const token = decodeURIComponent(p.slice("/door/t/".length));
      if (token) return ROUTE.token(token);
    }
    if (p === "/guest" || p === "/guest/") return ROUTE.guest();
    if (p === "/super" || p === "/super/") return ROUTE.super();

    // events
    if (p === "/super/events") return ROUTE.events();
    if (p === "/super/events/new") return ROUTE.eventNew();
    if (p.startsWith("/super/events/") && p.endsWith("/edit")) {
      const idStr = p.slice("/super/events/".length).replace(/\/edit$/, "");
      const id = Number(idStr);
      if (Number.isFinite(id)) return ROUTE.eventEdit(id);
    }

    // staff
    if (p === "/super/staff") return ROUTE.staff();
    if (p === "/super/staff/roles") return ROUTE.staffRoles();

    // comms
    if (p === "/super/messages") return ROUTE.messages();
    if (p === "/super/promo") return ROUTE.promo();
    if (p === "/super/settings") return ROUTE.settings();

    if (p === "/guest/eula") return ROUTE.eula();

    return ROUTE.root();
  },

  go(r: Route, replace = false) {
    const url = ROUTE.toPath(r);
    replace ? history.replaceState({}, "", url) : history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
  },
};

function stripBase(pathname: string) {
  const baseProbe = withBase("/").replace(/\/+$/, "");
  if (!baseProbe || baseProbe === "/") return pathname;
  return pathname.startsWith(baseProbe) ? pathname.slice(baseProbe.length) || "/" : pathname;
}

/** --- API endpoints for fetch (same base) --- */
export const API = {
  me:             () => `${API_BASE}/me`,
  debugEcho:      () => `${API_BASE}/debug/echo`,
  doorStats:      () => `${API_BASE}/door/stats`,
  entryVerify:    () => `${API_BASE}/entry/verify`,
  entryCheckin:   () => `${API_BASE}/entry/checkin`,
  entryDeny:      () => `${API_BASE}/entry/deny`,
  eventsList:     () => `${API_BASE}/events/list`,
  eventsCreate:   () => `${API_BASE}/events/create`,
  eventsGet:      () => `${API_BASE}/events/get`,
  eventsUpdate:   () => `${API_BASE}/events/update`,
};
